import isNumber from 'lodash/isNumber';

import type { IParameters, IPercentObject, IPaymentsAndOverpayFormulas } from '../models';

import { intervalFilter } from './credit-filters';

/* Формулы расчёта ежем. платежей для аннуитета, диффер., карточек овердрафта и ВКЛ */
export const paymentsFormulas: IPaymentsAndOverpayFormulas = {
    vkl: (S, P, n) => S / (n * 12) + (S * P) / 100 / 12,
    annuity: (S, P, n) => {
        const percentPerMonth = P / 100 / 12;

        return (
            Math.round(
                (S *
                    percentPerMonth *
                    (1 + percentPerMonth) *
                    (1 + percentPerMonth) ** (n * 12 - 1)) /
                    ((1 + percentPerMonth) ** (n * 12) - 1) /
                    10
            ) * 10
        );
    },
    dif: (S, P, n) => S / (n * 12) + (S * P) / 100 / 12,
    overdraft: (S, P) => S * (P / 100 / 365),
};

/* Возвращает сумму переплаты при аннуитетном платеже */
export const annuityCalculation = (
    amount: number,
    percent: number,
    term: number,
    payment?: number
) => {
    let currentCreditSum = amount;
    const creditPercent = percent / 100;
    let countTerm = 1;
    let overSum = 0;
    let sumPercent = 0;
    const now = new Date();
    let previousPaymentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let currentPaymentDate = new Date(now.getFullYear(), now.getMonth() + 1, now.getDate());
    if (payment) {
        while (countTerm <= term * 12) {
            sumPercent =
                (currentCreditSum *
                    creditPercent *
                    ((currentPaymentDate.getTime() - previousPaymentDate.getTime()) /
                        (1000 * 3600 * 24))) /
                365;
            overSum += sumPercent;
            currentCreditSum -= payment - sumPercent;
            previousPaymentDate = currentPaymentDate;
            currentPaymentDate = new Date(
                previousPaymentDate.getFullYear(),
                previousPaymentDate.getMonth() + 1,
                previousPaymentDate.getDate()
            );
            countTerm += 1;
        }
    }

    return overSum;
};

/* Возвращает сумму переплаты при диффер. платеже */
export const diffCalculation = (amount: number, percent: number, term: number) => {
    let currentCreditSum = amount;
    const creditPercent = percent / 100;
    let countTerm = 1;
    let overSum = 0;
    while (countTerm <= term * 12) {
        overSum += parseFloat(((currentCreditSum * creditPercent) / 12).toFixed(2));
        currentCreditSum -= parseFloat((amount / (term * 12)).toFixed(2));
        countTerm += 1;
    }

    return overSum;
};

/* Формулы расчёта переплаты при аннуитетном и дифференцированном платежах */
export const overpaymentsFormulas: IPaymentsAndOverpayFormulas = {
    annuity: (amount: number, percent: number, term: number, payment?: number) =>
        annuityCalculation(amount, percent, term, payment),
    dif: (amount: number, percent: number, term: number) => diffCalculation(amount, percent, term),
};

/**
 * Возвращает числовое начение процента для карточки
 * Если percent типа Number, то возвращает его
 * Если percent - это объект с массивом, то проходится по нему в поисках % исходя из текущих параметров
 * @example calculatePercentFromSum(11.5, params); // 11.5
 */
export const calculatePercentFromSum = (
    percent: number | IPercentObject,
    parameters: IParameters
) => {
    if (isNumber(percent) && Number.isFinite(percent)) {
        return percent;
    }
    /**
     * Пробегаемся интервальным фильтром исходя из допустимых значений по текущим
     * Если ничего не нашлось, возвращается первое допустимое значение
     * */
    if (typeof percent === 'object') {
        const percentArray = Object.entries(percent);
        const matchedPercent = percentArray.find(([, filters]) => {
            return filters.some(filter => {
                return Object.entries(filter).every(([key, values]) => {
                    if ((key === 'amount' && values) || (key === 'term' && values)) {
                        return intervalFilter(values, parameters[key]);
                    }

                    return false;
                });
            });
        });

        return parseFloat(matchedPercent ? matchedPercent[0] : percentArray[0][0]);
    }

    return 0;
};

/* Отображение первого параметра в карточке в зависимости от имеющихся данных */
export const calculateFirstHeading = (
    finalPercent: number | null,
    advFirstHeadingText?: string,
    advantageFirstHeading?: string
) => {
    if (advFirstHeadingText && finalPercent) {
        return `${advFirstHeadingText} ${finalPercent}%`;
    }
    if (!advFirstHeadingText && finalPercent) {
        return `${finalPercent}%`;
    }

    return advantageFirstHeading;
};
